import * as df from 'date-fns';

import API_ROOT from '../../../../../utils/api';
import securedAxios from '../../../../../utils/secured-axios';

import * as IBooks from '../../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../../interfaces/merchants/resources/books/inputs';
import * as IReserve from '../../../../../interfaces/reservations';

import { MerchantBookingData } from '../../../../../interfaces/merchants/bookings';
import {
  MerchantBookingAvailableDatesData,
  MerchantBookingAvailableTimesData,
} from '../../../../../interfaces/merchants/bookings/availabilities';
import { MerchantBookingConfirmData } from '../../../../../interfaces/merchants/bookings/confirm';
import {
  MerchantBookingCourseData,
  MerchantBookingSelectedCourseData,
} from '../../../../../interfaces/merchants/bookings/courses';
import { MerchantBookingCustomFieldsData } from '../../../../../interfaces/merchants/bookings/custom_fields';
import { MerchantBookingEventData } from '../../../../../interfaces/merchants/bookings/event';
import { MerchantBookingEventsData } from '../../../../../interfaces/merchants/bookings/events';
import { MerchantBookingLessonData } from '../../../../../interfaces/merchants/bookings/lessons';
import {
  MerchantBookingSelectedStaffData,
  MerchantBookingStaffData,
} from '../../../../../interfaces/merchants/bookings/staff';
import {
  MerchantBookingTimeSlotData,
  MerchantBookingTimeSlotsData,
} from '../../../../../interfaces/merchants/bookings/time_slots';

import * as b from './apiBodyBuilder';

export interface CalendarQuery {
  merchantId: string;
  resourceId: string;
  course: IBooks.Course;
  options: IBooks.Option[];
  staff?: IBooks.Staff;
  start: Date;
  end: Date;
}

export interface DateTimePickerQuery {
  merchantId: string;
  resourceId: string;
  lesson?: IBooks.Lesson;
  start: Date | null;
  end: Date | null;
}

export interface AvailableDateTimeQuery {
  merchantId: string;
  resourceId: string;
  course: IBooks.Course;
  options: IBooks.Option[];
  staff?: IBooks.Staff;
  date: Date;
}

interface PostPaymentResult {
  error?: stripe.StripeError;
  payment: IInputs.Payment;
}

export const fetchResource = async (merchantId: string, resourceId: string) => {
  const { data } = await securedAxios.get<MerchantBookingData>(
    `${API_ROOT}/merchants/${merchantId}/booking_pages/${resourceId}?formatted=true`,
  );
  return data;
};

export const fetchCourse = async (merchantId: string, resourceId: string) => {
  const { data } = await securedAxios.get<MerchantBookingCourseData>(
    `${API_ROOT}/merchants/${merchantId}/booking_pages/${resourceId}/courses`,
  );
  return data;
};

export const fetchSelectedCourse = async (
  merchantId: string,
  resouceId: string,
  CourseId: string,
): Promise<MerchantBookingSelectedCourseData | undefined> => {
  try {
    const { data } = await securedAxios.get<MerchantBookingSelectedCourseData>(
      `${API_ROOT}/merchants/${merchantId}/booking_pages/${resouceId}/courses/${CourseId}`,
    );
    return data;
  } catch (e) {
    if (e.response.status < 500) {
      return;
    }
    throw e;
  }
};

export const fetchLesson = async (merchantId: string, resourceId: string) => {
  const { data } = await securedAxios.get<MerchantBookingLessonData>(
    `${API_ROOT}/merchants/${merchantId}/booking_pages/${resourceId}/lessons`,
  );
  return data;
};

export const fetchStaff = async (merchantId: string, resourceId: string) => {
  const { data } = await securedAxios.get<MerchantBookingStaffData>(
    `${API_ROOT}/merchants/${merchantId}/booking_pages/${resourceId}/staff`,
  );
  return data;
};

export const fetchSelectedStaff = async (
  merchantId: string,
  resouceId: string,
  staffId: string,
): Promise<MerchantBookingSelectedStaffData | undefined> => {
  try {
    const { data } = await securedAxios.get<MerchantBookingSelectedStaffData>(
      `${API_ROOT}/merchants/${merchantId}/booking_pages/${resouceId}/staff/${staffId}`,
    );
    return data;
  } catch (e) {
    if (e.response.status < 500) {
      return;
    }
    throw e;
  }
};

const FORMAT = 'YYYYMMDDHHmm'; // 201811252001 2018/11/25 20:01

const toCalendarParam = (q: CalendarQuery): string =>
  [
    `course_id=${q.course.id}`,
    `start=${df.format(q.start, FORMAT)}`,
    `end=${df.format(q.end, FORMAT)}`,
  ]
    .concat(q.options.map(o => `option_ids[]=${o.id}`))
    .concat(q.staff ? `staff_id=${q.staff.id}` : '')
    .join('&');

export const fetchEvents = async (q: CalendarQuery) => {
  const { data } = await securedAxios.get<MerchantBookingEventsData>(
    `${API_ROOT}/merchants/${q.merchantId}/booking_pages/${
      q.resourceId
    }/events?${toCalendarParam(q)}`,
  );
  return data;
};

const toDatePickerParam = (q: DateTimePickerQuery): string =>
  [
    !!q.lesson ? `lesson_id=${q.lesson.id}` : null,
    !!q.start ? `start=${df.format(q.start, FORMAT)}` : null,
    !!q.end ? `end=${df.format(q.end, FORMAT)}` : null,
  ]
    .filter(a => !!a)
    .join('&');

export const fetchTimeSlots = async (
  q: DateTimePickerQuery,
): Promise<MerchantBookingTimeSlotsData> => {
  try {
    const { data } = await securedAxios.get<MerchantBookingTimeSlotsData>(
      `${API_ROOT}/merchants/${q.merchantId}/booking_pages/${
        q.resourceId
      }/time_slots?${toDatePickerParam(q)}`,
    );
    return data;
  } catch (e) {
    // ローディングがスタックしない様に空で返す。
    // TODO: 予約カレンダーでリロードを行う場合、要修正。
    return {
      meta: {
        staff: undefined,
        waiting_list_provided: false,
        waiting_listed: [],
      },
      data: [],
    };
  }
};

const toAvailableDateTimeParam = (q: AvailableDateTimeQuery): string =>
  [`course_id=${q.course.id}`, `date=${df.format(q.date, FORMAT)}`]
    .concat(q.options.map(o => `option_ids[]=${o.id}`))
    .concat(q.staff ? `staff_id=${q.staff.id}` : '')
    .join('&');

export const fetchAvailableDates = async (
  q: AvailableDateTimeQuery,
): Promise<MerchantBookingAvailableDatesData> => {
  const { data } = await securedAxios.get<MerchantBookingAvailableDatesData>(
    `${API_ROOT}/merchants/${q.merchantId}/booking_pages/${
      q.resourceId
    }/available_dates?${toAvailableDateTimeParam(q)}`,
  );
  return data;
};

export const fetchAvailableTimes = async (
  q: AvailableDateTimeQuery,
): Promise<MerchantBookingAvailableTimesData> => {
  const { data } = await securedAxios.get<MerchantBookingAvailableTimesData>(
    `${API_ROOT}/merchants/${q.merchantId}/booking_pages/${
      q.resourceId
    }/available_times?${toAvailableDateTimeParam(q)}`,
  );
  return data;
};

export const addToWaitingList = async (
  resourceId: string,
  timeSlot: IBooks.EventTimeSlot,
  lastName?: string,
  firstName?: string,
  email?: string,
): Promise<IReserve.WaitingListResponseData> => {
  try {
    const { data } = await securedAxios.post<{ success: boolean }>(
      `${API_ROOT}/reservations/waiting_list`,
      {
        resource_id: resourceId,
        time_slot_id: timeSlot.id,
        last_name: lastName,
        first_name: firstName,
        email,
      },
    );
    return data;
  } catch (e) {
    if (e.response.status === 400) {
      return e.response.data;
    }
  }
  return { success: false };
};

export const fetchCustomFields = async (
  merchantId: string,
  resourceId: string,
) => {
  const { data } = await securedAxios.get<MerchantBookingCustomFieldsData>(
    `${API_ROOT}/merchants/${merchantId}/booking_pages/${resourceId}/custom_fields`,
  );
  return data;
};

export const fetchConfirmation = async (
  merchantId: string,
  resourceId: string,
) => {
  const { data } = await securedAxios.get<MerchantBookingConfirmData>(
    `${API_ROOT}/merchants/${merchantId}/booking_pages/${resourceId}/confirm`,
  );
  return data;
};

export const fetchEvent = async (merchantId: string, resourceId: string) => {
  const { data } = await securedAxios.get<MerchantBookingEventData>(
    `${API_ROOT}/merchants/${merchantId}/booking_pages/${resourceId}/event`,
  );
  return data;
};

export const fetchSelectedTimeSlot = async (
  merchantId: string,
  resouceId: string,
  timeSlotId: string,
): Promise<MerchantBookingTimeSlotData | undefined> => {
  try {
    const { data } = await securedAxios.get<MerchantBookingTimeSlotData>(
      `${API_ROOT}/merchants/${merchantId}/booking_pages/${resouceId}/time_slots/${timeSlotId}`,
    );
    return data;
  } catch (e) {
    if (e.response.status < 500) {
      return;
    }
    throw e;
  }
};

export const fetchReservationPaymentMethods = async (reservationId: number) => {
  const { data } = await securedAxios.get<
    IReserve.ReservationsPaymentMethodsData
  >(`${API_ROOT}/reservations/${reservationId}/payment_methods`);
  return data;
};

const inputsToPostStructure = (inputs: IInputs.State): IReserve.Input => {
  if (inputs.course) {
    return inputs.staff
      ? {
          course_id: inputs.course.id.toString(),
          option_ids: inputs.options!.map(o => o.id.toString()),
          staff_id: inputs.staff.id.toString(),
        }
      : inputs.options
      ? {
          course_id: inputs.course.id.toString(),
          option_ids: inputs.options!.map(o => o.id.toString()),
        }
      : { course_id: inputs.course.id.toString() };
  }
  if (inputs.lesson) {
    return {
      lesson_id: inputs.lesson.id.toString(),
    };
  }
  if (inputs.eventTimeSlot) {
    return b.withEventTimeSlot(inputs.eventTimeSlot);
  }
  if (inputs.timeSlotAvailability) {
    return b.withTimeSlotAvailability(inputs.timeSlotAvailability);
  }
  if (inputs.schoolTimeSlots) {
    return b.withSchoolTimeSlot(inputs.schoolTimeSlots);
  }
  if (inputs.userInfo) {
    return b.withUserInfo(inputs.userInfo);
  }
  if (inputs.payment) {
    return b.withPayment(inputs.payment);
  }
  if (inputs.termsAccepted || inputs.selectedPaymentMethod) {
    return b.withFinalize(inputs.termsAccepted, inputs.selectedPaymentMethod);
  }
  return {};
};

const bookingFirstStepUrl = (
  modelType: IBooks.ModelType,
  reservationId: string,
): string => {
  if (modelType === IBooks.ModelType.EventScheme) {
    return `${API_ROOT}/reservations/${reservationId}/time_slots`;
  } else if (modelType === IBooks.ModelType.SchoolScheme) {
    return `${API_ROOT}/reservations/${reservationId}/lesson`;
  } else {
    return `${API_ROOT}/reservations/${reservationId}/course`;
  }
};

const _postReservations = async (
  url: string,
  reqData: IReserve.ReservationsRequestType,
): Promise<IReserve.ReservationsResponseData> => {
  try {
    const { data } = await securedAxios.post<IReserve.ReservationsResponseData>(
      url,
      reqData,
    );
    return data;
  } catch (e) {
    if (e.response.status === 400) {
      return { ...e.response.data, errorCode: e.response.status };
    } else {
      return { success: false, errorCode: e.response.status };
    }
    throw e;
  }
};

export const postReservationsStart = async (
  modelType: IBooks.ModelType,
  merchantId: string,
  resourceId: string,
  inputs: IInputs.State,
  withExpressFlow: boolean,
  reservationId?: number,
) =>
  reservationId
    ? _postReservations(
        bookingFirstStepUrl(modelType, reservationId.toString()),
        {
          input: await inputsToPostStructure(inputs),
        },
      )
    : _postReservations(`${API_ROOT}/reservations`, {
        input: await inputsToPostStructure(inputs),
        public_id: merchantId,
        resource_id: resourceId,
        booked_from: withExpressFlow ? 'express' : '',
      });

export const postReservationsCourseAndStaff = async (
  reservationId: number,
  inputs: IInputs.State,
) =>
  _postReservations(`${API_ROOT}/reservations/${reservationId}/course`, {
    input: await inputsToPostStructure(inputs),
  });

export const postReservationsBookingDateTime = async (
  reservationId: number,
  inputs: IInputs.State,
) =>
  _postReservations(
    `${API_ROOT}/reservations/${reservationId}/preferred_datetime`,
    {
      input: await inputsToPostStructure(inputs),
    },
  );

export const postReservationsLesson = async (
  reservationId: number,
  inputs: IInputs.State,
) =>
  _postReservations(`${API_ROOT}/reservations/${reservationId}/lesson`, {
    input: await inputsToPostStructure(inputs),
  });

export const postReservationsTimeSlot = async (
  reservationId: number,
  inputs: IInputs.State,
) =>
  _postReservations(`${API_ROOT}/reservations/${reservationId}/time_slot`, {
    input: await inputsToPostStructure(inputs),
  });

export const postReservationsTimeSlots = async (
  reservationId: number,
  inputs: IInputs.State,
) =>
  _postReservations(`${API_ROOT}/reservations/${reservationId}/time_slots`, {
    input: await inputsToPostStructure(inputs),
  });

export const postReservationsUserInfo = async (
  reservationId: number,
  inputs: IInputs.State,
) =>
  _postReservations(`${API_ROOT}/reservations/${reservationId}/customer`, {
    input: await inputsToPostStructure(inputs),
  });

export const postReservationsPayment = async (
  reservationId: number,
  inputs: IInputs.State,
) =>
  _postReservations(
    `${API_ROOT}/reservations/${reservationId}/payment/validate`,
    {
      input: await inputsToPostStructure(inputs),
    },
  );

export const postReservationsFinish = async (
  reservationId: number,
  inputs: IInputs.State,
) =>
  _postReservations(`${API_ROOT}/reservations/${reservationId}/finalize`, {
    input: await inputsToPostStructure(inputs),
  });

const callStripeApi = (
  payment: IInputs.Payment,
): Promise<PostPaymentResult> => {
  return new Promise(resolve => {
    Stripe.createToken(
      {
        cvc: payment.creditCard!.securityCode,
        exp_month: parseInt(payment.creditCard!.month!, 10),
        exp_year: parseInt(payment.creditCard!.year!, 10),
        number: payment.creditCard!.cardNumber!,
      },
      (status, response) => {
        if (status === 200) {
          resolve({ payment: { ...payment, token: response.id } });
        } else {
          resolve({ payment, error: response.error });
        }
      },
    );
  });
};

export const postPayment = async (
  payment: IInputs.Payment,
): Promise<PostPaymentResult> => {
  if (payment.paymentMethod.method !== 'credit_card') {
    return { payment };
  }
  if (!payment.cardSelection) {
    // カード選びがない => 利用歴がなく新しいカード生成
    return callStripeApi(payment);
  }
  if (payment.cardSelection === 'used') {
    // カード利用歴があり、以前のカードを利用する
    return { payment };
  }
  if (payment.cardSelection === 'another') {
    // カード利用歴があるが、新しく生成する
    return callStripeApi(payment);
  }
  throw new Error(`cannot handle with payment: ${payment}`);
};
